import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import React, { FC, useEffect, useMemo, useState, memo } from 'react';
import {
  Controller,
  FieldValues,
  RefCallBack,
  UseFormSetValue,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { InputNumber, Input } from 'components/atoms/input';
import { OptionProps } from 'components/atoms/select/types';
import { computePhoneNumbersList } from 'components/organisms/forms/utils';
import { StyledSelect } from 'components/organisms/tasks/field-value/styled-select';
import { HelperText } from 'components/styles';
import {
  FormItemWrapper,
  FormItemDesc,
  FormItemLabel,
  FieldsErrorRenderer,
  FormFieldProps,
} from './form-elements';

export const CountryCodeLabel = styled.span<{ isSelected?: boolean }>`
  padding-left: var(--spacing-03);
  ${HelperText};
  color: var(--gray-50);
  ${ifProp(
    'isSelected',
    css`
      color: var(--white-100);
    `,
  )};
`;

export const CountryCodeOption = memo((props: OptionProps): JSX.Element => {
  const { data, isSelected } = props;

  return (
    <components.Option {...props}>
      {data.label}
      <CountryCodeLabel isSelected={isSelected}>{data.value}</CountryCodeLabel>
    </components.Option>
  );
});

const StyledInputGroup = styled(Input.Group)`
  display: flex;
  margin-top: var(--spacing-03);
  ${InputNumber} {
    border-left: 1px solid var(--gray-50);
    flex-grow: 1;
  }
  .ant-input-number-handler-wrap {
    display: none;
  }
`;

const selectStyles = {
  menu: (base) => ({
    ...base,
    marginTop: 0,
    width: 272,
  }),
  control: (provided) => ({
    ...provided,
    width: 75,
    paddingRight: 'var(--spacing-03)',
  }),
};

const PhoneFieldRenderer = ({
  value,
  defaultCountryCode,
  ref,
  questionId,
  setValue,
  countryCodeList,
  disabled,
}: {
  value: string;
  defaultCountryCode: string;
  ref: RefCallBack;
  questionId: string;
  setValue?: UseFormSetValue<FieldValues>;
  countryCodeList: {
    label: string;
    value: string;
  }[];
  disabled: boolean;
}) => {
  const [selectValue, setSelectValue] = useState<{
    label: string;
    value: string;
  }>({
    label: defaultCountryCode,
    value: defaultCountryCode,
  });

  const [inputValue, setInputValue] = useState<number | undefined>();

  useEffect(() => {
    if (!value) {
      setInputValue(undefined);

      return;
    }

    const isValidPhoneNumber = value ? isPossiblePhoneNumber(value) : false;
    const phoneNumberValue = isValidPhoneNumber ? parsePhoneNumber(value) : '';

    if (isValidPhoneNumber && phoneNumberValue) {
      setSelectValue({
        label: `+${phoneNumberValue.countryCallingCode}`,
        value: `+${phoneNumberValue.countryCallingCode}`,
      });
      setInputValue(parseInt(phoneNumberValue.nationalNumber as string, 10));
    }
  }, [value]);

  useEffect(() => {
    if (!value) {
      setSelectValue({
        label: defaultCountryCode,
        value: defaultCountryCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountryCode]);

  const updateValue = (input, select) => {
    setValue?.(questionId, input ? `${select?.value}${input}` : '');
  };

  const onSelectChange = (v) => {
    setSelectValue(v);
    updateValue(inputValue, v);
  };
  const onInputChange = (v) => {
    const valueToUpdate = v ? Math.floor(Number(v)) : undefined;

    setInputValue(valueToUpdate);
    updateValue(v, selectValue);
  };

  return (
    <StyledInputGroup>
      <StyledSelect
        ref={ref}
        options={countryCodeList}
        isDisabled={disabled}
        value={{ label: selectValue.value }}
        placeholder={<FormattedMessage id="placeholder.search" />}
        styles={selectStyles}
        onChange={onSelectChange}
        components={{
          Option: CountryCodeOption,
          IndicatorSeparator: () => null,
          DropdownIndicator: ChevronDown16,
        }}
      />
      <InputNumber
        disabled={disabled}
        bordered={false}
        value={inputValue}
        ref={ref}
        onChange={onInputChange}
      />
    </StyledInputGroup>
  );
};

export const PhoneField: FC<FormFieldProps> = ({
  field,
  control,
  setValue,
  errors,
  disabled,
}) => {
  const countryCodeList = useMemo(() => computePhoneNumbersList(), []);

  const defaultCountryCode = field.questionMeta?.countryCode;

  return (
    <FormItemWrapper>
      <FormItemLabel label={field.questionText} required={field.required} />
      <Controller
        control={control}
        rules={{
          required: field.required,
          validate: {
            invalidPhoneNumber: (value) =>
              value ? isPossiblePhoneNumber(value) : true,
          },
        }}
        shouldUnregister
        name={field.questionId.toString()}
        render={(props) => {
          return (
            <PhoneFieldRenderer
              value={props.field.value}
              ref={props.field.ref}
              defaultCountryCode={
                defaultCountryCode ? `+${defaultCountryCode}` : ''
              }
              questionId={field.questionId.toString()}
              setValue={setValue}
              countryCodeList={countryCodeList}
              disabled={disabled}
            />
          );
        }}
      />

      <FieldsErrorRenderer errors={errors} questionId={field.questionId} />
      <FormItemDesc label={field.description} />
    </FormItemWrapper>
  );
};
